import { router } from "expo-router";
import type { ReactNode } from "react";
import { Platform, Pressable, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { CaptionText } from "~/components/ui/Text";
import { useAuth } from "~/hooks/useAuth";
import {
  DURANCE_MONTHLY_PRICE_TEXT,
  DURANCE_TRIAL_DAYS,
} from "~/services/revenuecat";
import { spacing, useTheme } from "~/styles/theme";

/**
 * Mobile only: counters sync only with an active subscription (a free trial
 * counts). Until then they live on this device and are never uploaded, and this
 * banner says so on every screen. The web app always requires a subscription,
 * so it never shows this.
 */
function useLocalOnly() {
  const { ready, configured, user } = useAuth();
  return Platform.OS !== "web" && ready && configured && !user?.hasSyncAccess;
}

function LocalDataBanner() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  return (
    <View
      style={{
        backgroundColor: colors.dangerBackground,
        borderBottomWidth: 1,
        borderBottomColor: colors.accent,
        paddingHorizontal: spacing.md,
        paddingTop: insets.top + spacing.xs,
        paddingBottom: spacing.xs,
        gap: spacing.xxs,
      }}
    >
      <CaptionText color={colors.ink}>
        Your counters are only saved on this device. Start your{" "}
        {DURANCE_TRIAL_DAYS}-day free trial ({DURANCE_MONTHLY_PRICE_TEXT}/month
        after) to back them up and sync across devices.
      </CaptionText>
      <Pressable
        accessibilityRole="link"
        accessibilityLabel="Start your free trial"
        onPress={() => router.push("/account")}
      >
        <CaptionText color={colors.accent} weight="semibold">
          Start free trial →
        </CaptionText>
      </Pressable>
    </View>
  );
}

export function LocalDataFrame({ children }: { children: ReactNode }) {
  const localOnly = useLocalOnly();
  if (!localOnly) return <>{children}</>;
  return (
    <View style={{ flex: 1 }}>
      <LocalDataBanner />
      <View style={{ flex: 1 }}>{children}</View>
    </View>
  );
}
